import React, { useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './Invoice.css';

const Invoice = () => {
  const invoiceRef = useRef();
  
  // Form state
  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyPhone: '',
    companyEmail: '',
    companyLogo: null,
    
    // Client Information
    clientName: '',
    clientAddress: '',
    clientCity: '',
    clientState: '',
    clientZip: '',
    clientEmail: '',
    
    // Invoice Details
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    
    // Items
    items: [
      { description: '', quantity: 1, rate: 0, amount: 0 }
    ],
    
    // Additional Details
    notes: '',
    termsAndConditions: 'Payment is due within 30 days of invoice date. Late payments may incur additional charges.',
    
    // Calculated fields
    subtotal: 0,
    taxRate: 0,
    taxAmount: 0,
    total: 0
  });

  const [showPreview, setShowPreview] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('company');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        setFormData(prev => ({
          ...prev,
          companyLogo: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    // Calculate amount for the item
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals
    calculateTotals(updatedItems);
  };

  // Add new item row
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  // Remove item row
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
      calculateTotals(updatedItems);
    }
  };

  // Calculate totals
  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = (subtotal * formData.taxRate) / 100;
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      total
    }));
  };

  // Handle tax rate change
  const handleTaxRateChange = (e) => {
    const taxRate = parseFloat(e.target.value) || 0;
    const taxAmount = (formData.subtotal * taxRate) / 100;
    const total = formData.subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      taxRate,
      taxAmount,
      total
    }));
  };

  // Generate and download PDF
  const downloadPDF = async () => {
    const element = invoiceRef.current;
    
    // Create a temporary clone for PDF generation with fixed styling
    const clone = element.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.width = '794px'; // A4 width in pixels at 96 DPI
    clone.style.backgroundColor = 'white';
    clone.style.fontFamily = 'Arial, sans-serif';
    
    document.body.appendChild(clone);
    
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      height: clone.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: 794,
      windowHeight: clone.scrollHeight
    });
    
    // Remove the temporary clone
    document.body.removeChild(clone);
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save(`invoice-${formData.invoiceNumber || 'draft'}.pdf`);
  };

  return (
    <div className="invoice-generator">
      <div className="container">
        <header className="page-header">
          <h1>Invoice Generator</h1>
          <p>Create professional invoices with ease</p>
        </header>
        
        {!showPreview ? (
          <div className="invoice-form-container">
            
            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button 
                className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
                onClick={() => setActiveTab('company')}
              >
                <span className="tab-number">1</span>
                Company Details
              </button>
              <button 
                className={`tab-btn ${activeTab === 'client' ? 'active' : ''}`}
                onClick={() => setActiveTab('client')}
              >
                <span className="tab-number">2</span>
                Bill To
              </button>
              <button 
                className={`tab-btn ${activeTab === 'invoice' ? 'active' : ''}`}
                onClick={() => setActiveTab('invoice')}
              >
                <span className="tab-number">3</span>
                Invoice Details
              </button>
              <button 
                className={`tab-btn ${activeTab === 'items' ? 'active' : ''}`}
                onClick={() => setActiveTab('items')}
              >
                <span className="tab-number">4</span>
                Items & Billing
              </button>
            </div>

            <div className="form-content">
              
              {/* Company Information Tab */}
              {activeTab === 'company' && (
                <div className="tab-panel">
                  <h2>Company Information</h2>
                  
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Company Logo (Optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="form-control file-input"
                      />
                      {logoPreview && (
                        <div className="logo-preview">
                          <img src={logoPreview} alt="Company Logo" />
                        </div>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label>Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                        placeholder="Enter company name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                        placeholder="company@example.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        name="companyPhone"
                        value={formData.companyPhone}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Street Address</label>
                      <input
                        type="text"
                        name="companyAddress"
                        value={formData.companyAddress}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="123 Business Street"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="companyCity"
                        value={formData.companyCity}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="New York"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>State/Province</label>
                      <input
                        type="text"
                        name="companyState"
                        value={formData.companyState}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="NY"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>ZIP/Postal Code</label>
                      <input
                        type="text"
                        name="companyZip"
                        value={formData.companyZip}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="10001"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Client Information Tab */}
              {activeTab === 'client' && (
                <div className="tab-panel">
                  <h2>Bill To</h2>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Client/Company Name *</label>
                      <input
                        type="text"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                        placeholder="Client Company Name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="clientEmail"
                        value={formData.clientEmail}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="client@example.com"
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Street Address</label>
                      <input
                        type="text"
                        name="clientAddress"
                        value={formData.clientAddress}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="456 Client Avenue"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="clientCity"
                        value={formData.clientCity}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Los Angeles"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>State/Province</label>
                      <input
                        type="text"
                        name="clientState"
                        value={formData.clientState}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="CA"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>ZIP/Postal Code</label>
                      <input
                        type="text"
                        name="clientZip"
                        value={formData.clientZip}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="90210"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice Details Tab */}
              {activeTab === 'invoice' && (
                <div className="tab-panel">
                  <h2>Invoice Details</h2>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Invoice Number *</label>
                      <input
                        type="text"
                        name="invoiceNumber"
                        value={formData.invoiceNumber}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                        placeholder="INV-2025-001"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Invoice Date *</label>
                      <input
                        type="date"
                        name="invoiceDate"
                        value={formData.invoiceDate}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Due Date</label>
                      <input
                        type="date"
                        name="dueDate"
                        value={formData.dueDate}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Notes</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="3"
                        className="form-control"
                        placeholder="Additional notes or project details..."
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Terms and Conditions</label>
                      <textarea
                        name="termsAndConditions"
                        value={formData.termsAndConditions}
                        onChange={handleInputChange}
                        rows="3"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Items and Billing Tab */}
              {activeTab === 'items' && (
                <div className="tab-panel">
                  <h2>Items & Billing</h2>
                  
                  <div className="items-section">
                    <div className="items-header">
                      <div>Description</div>
                      <div>Qty</div>
                      <div>Rate (₹)</div>
                      <div>Amount (₹)</div>
                      <div></div>
                    </div>
                    
                    {formData.items.map((item, index) => (
                      <div key={index} className="item-row">
                        <div className="form-group">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Service or product description"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            min="1"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="form-control"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="amount-display">
                          ₹{item.amount.toFixed(2)}
                        </div>
                        <div className="item-actions">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn-remove"
                            disabled={formData.items.length === 1}
                            title="Remove item"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={addItem}
                      className="btn-add-item"
                    >
                      + Add Item
                    </button>
                  </div>
                  
                  <div className="billing-summary">
                    <div className="tax-section">
                      <label>Tax Rate (%)</label>
                      <input
                        type="number"
                        value={formData.taxRate}
                        onChange={handleTaxRateChange}
                        min="0"
                        step="0.01"
                        className="form-control tax-input"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="totals-summary">
                      <div className="total-line">
                        <span>Subtotal:</span>
                        <span>₹{formData.subtotal.toFixed(2)}</span>
                      </div>
                      {formData.taxRate > 0 && (
                        <div className="total-line">
                          <span>Tax ({formData.taxRate}%):</span>
                          <span>₹{formData.taxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="total-line final-total">
                        <span>Total:</span>
                        <span>₹{formData.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="form-navigation">
                {activeTab !== 'company' && (
                  <button
                    type="button"
                    onClick={() => {
                      const tabs = ['company', 'client', 'invoice', 'items'];
                      const currentIndex = tabs.indexOf(activeTab);
                      setActiveTab(tabs[currentIndex - 1]);
                    }}
                    className="btn btn-secondary"
                  >
                    Previous
                  </button>
                )}
                
                {activeTab !== 'items' ? (
                  <button
                    type="button"
                    onClick={() => {
                      const tabs = ['company', 'client', 'invoice', 'items'];
                      const currentIndex = tabs.indexOf(activeTab);
                      setActiveTab(tabs[currentIndex + 1]);
                    }}
                    className="btn btn-primary"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="btn btn-primary"
                    disabled={!formData.companyName || !formData.clientName || !formData.invoiceNumber}
                  >
                    Preview Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="invoice-preview-container">
            <div className="preview-header">
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-secondary"
              >
                ← Edit Invoice
              </button>
              <button
                onClick={downloadPDF}
                className="btn btn-primary"
              >
                Download PDF
              </button>
            </div>
            
            {/* Professional Invoice Preview */}
            <div ref={invoiceRef} className="invoice-document">
              
              {/* Decorative Elements */}
              <div className="decorative-elements">
                <div className="geometric-shape shape-1"></div>
                <div className="geometric-shape shape-2"></div>
                <div className="geometric-shape shape-3"></div>
              </div>
              
              {/* Main Invoice Container */}
              <div className="invoice-container">
                
                {/* Header Section */}
                <div className="invoice-header-section">
                  <div className="company-info-left">
                    {formData.companyLogo && (
                      <div className="company-logo-container">
                        <img src={formData.companyLogo} alt="Company Logo" />
                      </div>
                    )}
                    <div className="company-details-left text-left">
                      <h1 className="company-name-header">{formData.companyName}</h1>
                      {formData.companyAddress && <div className="company-address">{formData.companyAddress}</div>}
                      <div className="company-location">
                        {[formData.companyCity, formData.companyState, formData.companyZip].filter(Boolean).join(' ')}
                      </div>
                      <div className="company-country">U.S.A</div>
                    </div>
                  </div>
                  
                  <div className="invoice-title-section">
                    <h1 className="invoice-main-title">INVOICE</h1>
                  </div>
                </div>

                {/* Invoice Details Table */}
                <table className="invoice-details-table ">
                  <tbody>
                    <tr>
                      <td className="detail-label">Invoice#</td>
                      <td className="detail-value">{formData.invoiceNumber}</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Invoice Date</td>
                      <td className="detail-value">{new Date(formData.invoiceDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Terms</td>
                      <td className="detail-value">Due on Receipt</td>
                    </tr>
                    {formData.dueDate && (
                      <tr>
                        <td className="detail-label">Due Date</td>
                        <td className="detail-value">{new Date(formData.dueDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Bill To and Ship To Section */}
                <div className="billing-section">
                  <div className="bill-to-section text-left">
                    <div className="section-header ">Bill To</div>
                    <div className="client-details ">
                      <div className="client-name-bold">{formData.clientName}</div>
                      {formData.clientAddress && <div>{formData.clientAddress}</div>}
                      <div>{formData.clientCity}</div>
                      <div>{formData.clientZip} {formData.clientState}</div>
                    </div>
                  </div>
                  
                  <div className="ship-to-section text-left">
                    <div className="section-header">Ship To</div>
                    <div className="ship-details">
                      {formData.clientAddress && <div>{formData.clientAddress}</div>}
                      <div>{formData.clientCity}</div>
                      <div>{formData.clientZip} {formData.clientState}</div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="professional-items-table">
                  <thead>
                    <tr className="table-header-row">
                      <th className="item-number">#</th>
                      <th className="item-description">Item & Description</th>
                      <th className="item-qty">Qty</th>
                      <th className="item-rate">Rate</th>
                      <th className="item-amount">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="item-row-data">
                        <td className="item-number-cell">{index + 1}</td>
                        <td className="item-description-cell">
                          <div className="item-title">{item.description}</div>
                          {/* <div className="item-subtitle">Professional service description</div> */}
                        </td>
                        <td className="item-qty-cell">{item.quantity.toFixed(2)}</td>
                        <td className="item-rate-cell">₹{item.rate.toFixed(2)}</td>
                        <td className="item-amount-cell">₹{item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Footer Section */}
                <div className="invoice-footer-section">
                  <div className="footer-left text-left">
                    <div className="thanks-message">Thanks for shopping with us.</div>
                    
                    <div className="terms-conditions-section">
                      <div className="terms-title">Terms & Conditions</div>
                      <div className="terms-content">
                        Full payment is due by the due date of this invoice.<br/>
                        Late payments may incur additional charges or<br/>
                        interest as per the applicable laws.
                      </div>
                    </div>
                  </div>
                  
                  <div className="footer-right">
                    <table className="totals-summary-table">
                      <tbody>
                        <tr>
                          <td className="total-label-cell"></td>
                          <td className="total-label ">Sub Total</td>
                          <td className="total-amount">₹{formData.subtotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <div className="tax-total-section">
                      <div className="tax-row">
                        <span className="tax-label">Tax Rate</span>
                        <span className="tax-value">{formData.taxRate.toFixed(2)}%</span>
                      </div>
                      <div className="total-row">
                        <span className="total-label-final">Total</span>
                        <span className="total-amount-final">₹{formData.total.toFixed(2)}</span>
                      </div>
                      <div className="balance-due-row">
                        <span className="balance-label">Balance Due</span>
                        <span className="balance-amount">₹{formData.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoice;
